import Book from '@mui/icons-material/Book'
import Clear from '@mui/icons-material/Clear'
import Description from '@mui/icons-material/Description'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridFilterItem,
  type GridFilterOperator,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import qs from 'query-string'
import React, { useRef } from 'react'
import { useEnfucePayoutsV3, useUpdateEnfucePayoutV3 } from '../api/react-query'
import config from '../config'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import useLanguage from '../flamingo/hooks/useLanguage'
import { TableHeader, useTableGridProps } from '../helpers/getTableOptions'
import { getText } from '../helpers/getText'
import { getPayoutsActions } from '../helpers/PayoutsActionsHelper'
import { formatCurrency } from '../helpers/utils'
import TextKeys from '../libs/TextKeys'
import { BackOfficeEnfucePayoutStateV3, type BackOfficePayoutDataResponseDtoV3 } from '../types/payoutV3'
import JSONDetailPanel from './JSONDetailPanel'
import TableWrapper from './TableWrapper'

const STATE = 'state'

// The default `contains` filter renders its input at `size="small"`, which is shorter than
// the header filter row's other controls (see `createStateFilterOperator` below) and looks
// clipped alongside them. Reuse the default filter logic, just with a full-size input.
// getGridStringOperators()[0] is always the 'contains' operator.
const containsFilterFn = getGridStringOperators()[0].getApplyFilterFn

const createTextFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: 'contains',
  getApplyFilterFn: containsFilterFn,
  InputComponent: ({ applyValue, ...params }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const itemRef = useRef(params.item)
    itemRef.current = params.item

    return (
      <Box sx={{ width: '100%', paddingTop: 2 }}>
        <TextField
          fullWidth
          label={label}
          defaultValue={params.item.value ?? ''}
          onChange={(event) => {
            clearTimeout(timerRef.current)
            const value = event.target.value
            timerRef.current = setTimeout(() => applyValue({ ...itemRef.current, value }), 300)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </Box>
    )
  },
  headerLabel: label,
})

const createStateFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: 'customArrayContains',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    const filter = Array.from(filterItem.value ?? [])

    if (filter.length === 0) return null

    return (value: unknown) => filter.includes(value)
  },
  InputComponent: ({ applyValue, clearButton, ...params }) => {
    const selected = (params.item.value as string[] | undefined) ?? []
    const labelId = `card-payouts-filter-${params.item.field}`

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
        <FormControl fullWidth sx={{ minWidth: 0 }}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            label={label}
            multiple
            value={selected}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              const value = event.target.value
              applyValue({ ...params.item, value: typeof value === 'string' ? value.split(',') : value })
            }}
            renderValue={(values) => (values.length === 0 ? '' : `${values.length} selected`)}
            sx={{
              '&& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                padding: '12.5px 32px 12.5px 12.5px',
              },
            }}
          >
            {Object.values(BackOfficeEnfucePayoutStateV3).map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selected.includes(option)} size="small" sx={{ pointerEvents: 'none', padding: 0, marginRight: 1 }} />
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected.length > 0 ? clearButton : null}
      </Box>
    )
  },
  headerLabel: label,
})

const ACTION_ICONS = [<Book key="book" />, <Description key="description" />, <Clear key="clear" />]

export function CardPayoutsTable({ customerId }: { customerId?: string }) {
  const language = useLanguage()
  const mutationUpdate = useUpdateEnfucePayoutV3()
  const { id } = qs.parse(window.location.search) as Record<string, string>

  const [stateFilter, setStateFilter] = React.useState([
    BackOfficeEnfucePayoutStateV3.CREATE_PAYOUT,
    BackOfficeEnfucePayoutStateV3.DRAFT,
    BackOfficeEnfucePayoutStateV3.BOOKED,
  ])

  const { data: payouts = [] } = useEnfucePayoutsV3(customerId, stateFilter)

  const gridProps = useTableGridProps({ headerTextKey: TextKeys.accountingPayoutsDate })

  const apiRef = useGridApiRef()

  const rowActions = getPayoutsActions({
    mutation: (payout) => mutationUpdate.mutateAsync(payout),
    isLoading: mutationUpdate.isLoading,
    language,
  })

  const columns: GridColDef<BackOfficePayoutDataResponseDtoV3>[] = [
    { field: 'id', headerName: 'id', flex: 1 },
    {
      headerName: getText(TextKeys.customersCardPayoutsTableBookedLabel, language),
      field: 'bookDate',
      flex: 1,
      filterOperators: [createTextFilterOperator(getText(TextKeys.customersCardPayoutsTableBookedLabel, language))],
    },
    {
      headerName: getText(TextKeys.customersCardPayoutsTableLableState, language),
      field: STATE,
      flex: 1,
      filterOperators: [createStateFilterOperator(getText(TextKeys.customersCardPayoutsTableLableState, language))],
    },
    {
      headerName: getText(TextKeys.customersCardPayoutsTableLableCustomer, language),
      field: 'companyName',
      flex: 1,
      filterOperators: [createTextFilterOperator(getText(TextKeys.customersCardPayoutsTableLableCustomer, language))],
    },
    {
      headerName: getText(TextKeys.customersCardPayoutsTableLablePayoutType, language),
      field: 'payoutType',
      flex: 1,
      filterOperators: [createTextFilterOperator(getText(TextKeys.customersCardPayoutsTableLablePayoutType, language))],
    },
    {
      headerName: getText(TextKeys.customersCardPayoutsTableLableSubtype, language),
      field: 'enfucePayoutType',
      flex: 1,
      filterOperators: [createTextFilterOperator(getText(TextKeys.customersCardPayoutsTableLableSubtype, language))],
    },
    {
      headerName: 'Amount',
      field: 'amount.value',
      flex: 1,
      valueGetter: (_, row) => formatCurrency(row.amount),
      filterOperators: [createTextFilterOperator('Amount')],
    },
    {
      headerName: 'Currency',
      field: 'amount.currency',
      flex: 1,
      valueGetter: (_, row) => row.amount?.currency,
      filterOperators: [createTextFilterOperator('Currency')],
    },
    {
      field: 'actions',
      type: 'actions',
      // The default actions column is a fixed 100px, too narrow for all 3 actions (book, settle, cancel).
      width: 120,
      getActions: ({ row }) =>
        rowActions.map((action, index) => (
          <Tooltip key={action.tooltip} title={action.tooltip}>
            {/* A disabled IconButton swallows pointer events, so Tooltip needs this span to stay hoverable. */}
            <span>
              <GridActionsCellItem
                icon={ACTION_ICONS[index]}
                label={action.tooltip}
                disabled={action.disabled}
                onClick={(event) => action.onClick(event, row)}
              />
            </span>
          </Tooltip>
        )),
    },
  ]

  return (
    <TableWrapper>
      <TableHeader title={getText(TextKeys.customersCardPayoutsPayoutsHeading, language)} apiRef={apiRef} initialSearch={id} />
      <DataGridPremium
        {...gridProps}
        apiRef={apiRef}
        rows={payouts}
        columns={columns}
        headerFilters={config.enableCardPayoutFiltering}
        disableColumnFilter
        headerFilterHeight={81}
        columnVisibilityModel={{ id: false }}
        getDetailPanelContent={({ row }) => <JSONDetailPanel src={row} />}
        getDetailPanelHeight={() => 'auto'}
        initialState={{
          ...gridProps.initialState,
          sorting: { sortModel: [{ field: 'bookDate', sort: 'desc' }] },
          filter: {
            filterModel: {
              items: [{ id: 0, field: STATE, operator: 'customArrayContains', value: stateFilter }],
              quickFilterValues: id ? [id] : [],
            },
          },
        }}
        onFilterModelChange={(model) => {
          const state = model.items.find((item) => item.field === STATE)
          if (state) setStateFilter(state.value)
        }}
        slots={{
          headerFilterMenu: null,
        }}
        slotProps={{ headerFilterCell: { showClearIcon: true } }}
        sx={{
          ...gridProps.sx,
          '& .MuiDataGrid-columnHeader': { backgroundColor: ColorsDeprecated.surfaceOnWhite },
        }}
      />
    </TableWrapper>
  )
}
